/**
 * Authentication API schemas
 */

import { z } from 'zod';
import { OpaqueCursorPaginationSchema } from '../authorization.types';
import { Role, Status } from '@pillar-portal/database/dist/enums';
import { CredentialType } from '../secret.types';
import { IpgSystemId } from '../types/third-party-system.enums';
import { PaymentRequestStatus } from '../payment.types';
import type {
    ActiveRoleAssignmentResponse,
    CheckProposedPasswordRejectedReason,
    CheckProposedPasswordRequest,
    CheckProposedPasswordResponse,
    CompleteCustomerPaymentLinkAttachmentRequest,
    EmailMfaActivationTokenPayload,
    EmailVerificationTokenPayload,
    InvitationTokenPayload,
    ListSingleUseTokensRequest,
    ListSingleUseTokensResponse,
    LoginRequest,
    LoginResponse,
    MfaActivationRequestResponse,
    MfaLoginChallengeResponse,
    MfaMethod,
    MfaSetupStatusResponse,
    MfaPolicyStatusResponse,
    PaymentRequestAccessTokenExecutionScope,
    PaymentRequestAccessTokenPaymentConfigurationSnapshot,
    PaymentRequestAccessTokenPayload,
    PillarLoginLinkTokenPayload,
    SingleUseTokenListItem,
    SingleUseTokenOrganizationSummary,
    SingleUseTokenPaymentRequestSummary,
    SingleUseTokensSearchRequest,
    SingleUseAuthTokenType,
    SetDefaultRoleAssignmentRequest,
    SetDefaultRoleAssignmentUuidRequest,
    SetActiveRoleAssignmentUuidRequest,
    SetActiveRoleAssignmentResponse,
    SessionUserProfileDetailedResponse,
    SetupTotpResponse,
    SwapCookieForTokenResponse,
    SwapTokenForCookieRequest,
    SwapTokenForCookieResponse,
    TotpSetupActivationTarget,
    SmsMfaActivationTokenPayload,
    SmsVerificationTokenPayload,
    EnableTotpRequest,
    VerifyMfaActivationRequest,
    VerifyEmailResponse,
    VerifyMfaLoginRequest,
    VerifyMfaLoginResponse,
    VerifyTotpRequest,
    VerifyTotpResponse,
} from '../auth.types';
import { PermissionGrantArraySchema, RoleAssignmentDetailedSchema } from '../permission.types';
import { CombinedConfigResponseSchema } from './samba-config.schemas';
import { SearchTermsSchema } from './omnisearch.schemas';
import { PhoneNumberSchema } from './common.schemas';

/**
 * Login request
 */
export const LoginRequestSchema: z.ZodType<LoginRequest> = z.object({
    email: z.string().email(),
    password: z.string().min(1),
    payment_request_uuid: z.string().uuid().optional(),
});

/**
 * Login response
 * If requires_totp is true, user must verify TOTP before being fully authenticated.
 * If requires_totp_setup is true, the authenticated user must enable TOTP
 * before continuing under the already-activated role assignment.
 */
export const TotpSetupActivationTargetSchema: z.ZodType<TotpSetupActivationTarget> = z.object({
    role_assignment_uuid: z.string().uuid(),
    organization_uuid: z.string().uuid().nullable(),
    organization_name: z.string().nullable(),
});

export const MfaMethodSchema: z.ZodType<MfaMethod> = z.enum(['TOTP', 'EMAIL', 'SMS']);

export const MfaSetupStatusResponseSchema: z.ZodType<MfaSetupStatusResponse> = z
    .object({
        activation_target: TotpSetupActivationTargetSchema.nullable(),
        available_mfa_setup_methods: z.array(MfaMethodSchema),
    })
    .strict();

export const LoginResponseSchema: z.ZodType<LoginResponse> = z.object({
    message: z.string(),
    requires_totp: z.boolean(),
    requires_mfa: z.boolean().default(false),
    available_mfa_methods: z.array(MfaMethodSchema).default([]),
    requires_mfa_setup: z.boolean().default(false),
    available_mfa_setup_methods: z.array(MfaMethodSchema).default([]),
    requires_totp_setup: z.boolean().default(false),
    requires_password_change: z.boolean().default(false),
    mfa_setup_activation_target: TotpSetupActivationTargetSchema.optional(),
    totp_setup_activation_target: TotpSetupActivationTargetSchema.optional(),
    user: z
        .object({
            user_uuid: z.string().uuid(),
            primary_email: z.string(),
            emails: z.array(z.string()),
            status: z.enum(Status),
            totp_enabled: z.boolean(),
        })
        .optional(),
});

export const MfaPolicyStatusResponseSchema: z.ZodType<MfaPolicyStatusResponse> = z
    .object({
        required: z.boolean(),
        totp_enabled: z.boolean(),
        email_allowed: z.boolean(),
        sms_allowed: z.boolean(),
        available_login_methods: z.array(MfaMethodSchema),
        primary_email_available: z.boolean(),
        primary_phone_available: z.boolean(),
        can_disable: z.boolean(),
    })
    .strict();

export const MfaActivationRequestResponseSchema: z.ZodType<MfaActivationRequestResponse> = z
    .object({
        message: z.string().min(1),
        delivery_destination: z.string().min(1),
        expires_at: z.string().datetime(),
    })
    .strict();

export const MfaLoginChallengeResponseSchema: z.ZodType<MfaLoginChallengeResponse> =
    MfaActivationRequestResponseSchema;

export const VerifyMfaActivationRequestSchema: z.ZodType<VerifyMfaActivationRequest> = z
    .object({
        code: z.string().min(1),
    })
    .strict();

export const VerifyMfaLoginRequestSchema: z.ZodType<VerifyMfaLoginRequest> =
    VerifyMfaActivationRequestSchema;

export const EmailMfaActivationTokenPayloadSchema: z.ZodType<EmailMfaActivationTokenPayload> = z
    .object({
        user_id: z.number().int().positive(),
        email: z.string().email(),
    })
    .strict();

export const SmsMfaActivationTokenPayloadSchema: z.ZodType<SmsMfaActivationTokenPayload> = z
    .object({
        user_id: z.number().int().positive(),
        phone: z.string().min(1),
    })
    .strict();

export const SmsVerificationTokenPayloadSchema: z.ZodType<SmsVerificationTokenPayload> = z
    .object({
        user_id: z.number().int().positive(),
        phone: z.string().min(1),
    })
    .strict();

export const SessionUserProfileDetailedResponseSchema: z.ZodType<SessionUserProfileDetailedResponse> =
    z.object({
        user_uuid: z.string().uuid(),
        first_name: z.string().nullable(),
        last_name: z.string().nullable(),
        primary_email: z.string(),
        emails: z.array(z.string()),
        phones: z.array(z.string()).default([]),
        primary_phone: z.string().nullable().default(null),
        totp_enabled: z.boolean(),
        mfa_email_allowed: z.boolean().default(false),
        mfa_sms_allowed: z.boolean().default(false),
        status: z.enum(Status),
        requires_password_change: z.boolean().default(false),
        invitation_token: z.string().optional(),
        default_role_assignment: RoleAssignmentDetailedSchema.nullable(),
        other_role_assignments: z.array(RoleAssignmentDetailedSchema),
    });

export const ActivePermissionsBundleResponseSchema: z.ZodType<
    ActiveRoleAssignmentResponse['permissions_bundle']
> = z.object({
    permissions_bundle_uuid: z.string().uuid(),
    name: z.string(),
    description: z.string().nullable(),
    permissions: PermissionGrantArraySchema,
    is_personal: z.boolean(),
    status: z.enum(Status),
});

export const ActiveRoleAssignmentResponseSchema: z.ZodType<ActiveRoleAssignmentResponse> = z.object(
    {
        role_assignment_uuid: z.string().uuid(),
        organization_uuid: z.string().uuid().nullable(),
        status: z.enum(Status),
        role: z.enum(Role),
        organization_name: z.string().nullable(),
        organization_is_iso: z.boolean(),
        organization_is_merchant: z.boolean(),
        organization_is_customer: z.boolean(),
        permissions_bundle: ActivePermissionsBundleResponseSchema,
        combined_config: CombinedConfigResponseSchema,
    }
);

export const CompleteCustomerPaymentLinkAttachmentRequestSchema: z.ZodType<CompleteCustomerPaymentLinkAttachmentRequest> =
    z.object({
        payment_request_uuid: z.string().uuid(),
    });

export const CheckProposedPasswordRejectedReasonSchema: z.ZodType<CheckProposedPasswordRejectedReason> =
    z.union([
        z.literal('PASSWORD_TOO_LONG'),
        z.literal('DISALLOWED_DEFAULT_PASSWORD'),
        z.literal('ZXCVBN_TOO_WEAK'),
        z.literal('COMPROMISED_PASSWORD_CORPUS_MATCH'),
        z.literal('COMPROMISED_PASSWORD_CORPUS_UNAVAILABLE'),
    ]);

export const CheckProposedPasswordRequestSchema: z.ZodType<CheckProposedPasswordRequest> = z
    .object({
        password: z.string().min(1),
    })
    .strict();

export const CheckProposedPasswordResponseSchema: z.ZodType<CheckProposedPasswordResponse> = z
    .object({
        accepted: z.boolean(),
        rejected_reasons: z.array(CheckProposedPasswordRejectedReasonSchema),
    })
    .strict();

/**
 * Verify TOTP request
 */
export const VerifyTotpRequestSchema: z.ZodType<VerifyTotpRequest> = z.object({
    totp_code: z.string().min(1),
});

/**
 * Verify TOTP response
 */
export const VerifyTotpResponseSchema: z.ZodType<VerifyTotpResponse> = z.object({
    user_uuid: z.string().uuid(),
    primary_email: z.string(),
    roles: z.array(z.string()),
});

export const VerifyMfaLoginResponseSchema: z.ZodType<VerifyMfaLoginResponse> =
    VerifyTotpResponseSchema;

/**
 * Verify email request
 */
export const VerifyEmailRequestSchema = z.object({
    token: z.string().min(1),
    password: z.string().optional(), // Required for PENDING users
});

/**
 * Verify email response
 */
export const VerifyEmailResponseSchema: z.ZodType<VerifyEmailResponse> = z.object({
    message: z.string(),
    user: z.object({
        user_uuid: z.string().uuid(),
        primary_email: z.string().nullable(),
        status: z.enum(Status),
    }),
});

export const CreateCustomerSelfOnboardingRequestSchema = z.object({
    email: z.string().email(),
    first_name: z.string().trim().min(1),
    last_name: z.string().trim().min(1),
    password: z.string().min(8),
    payment_request_uuid: z.string().uuid().optional(),
    store_payment_method_for_future_use: z.boolean().optional(),
});

export const CreateCustomerSelfOnboardingResponseSchema = z.object({
    message: z.string(),
    primary_email: z.string().email(),
    customer_organization_uuid: z.string().uuid(),
});

export const InvitationTokenPayloadSchema: z.ZodType<InvitationTokenPayload> = z.object({
    primary_email: z.string().email(),
    role: z.enum(Role),
    organization_id: z.number().int().positive().nullable(),
    first_name: z.string().nullable(),
    last_name: z.string().nullable(),
    emails: z.array(z.string().email()).optional(),
    phones: z.array(z.string().min(1)).optional(),
    primary_phone: z.string().min(1).nullable().optional(),
    policies: z.array(z.string().uuid()).optional(),
    policy_bundle_ids: z.array(z.number().int().positive()).optional(),
});

export const EmailVerificationTokenPayloadSchema: z.ZodType<EmailVerificationTokenPayload> =
    z.object({
        user_id: z.number().int().positive(),
        email: z.string().email(),
    });

export const PaymentRequestAccessTokenPaymentConfigurationSnapshotSchema: z.ZodType<PaymentRequestAccessTokenPaymentConfigurationSnapshot> =
    z.object({
        account_id: z.string().min(1),
        credential_uuid: z.string().uuid(),
        credential_type: z.enum([
            CredentialType.VIRTUAL_TERMINAL,
            CredentialType.PHYSICAL_TERMINAL,
        ]),
        is_virtual: z.boolean().nullable(),
        allow_surcharge: z.boolean(),
        allow_convenience_fee: z.boolean(),
        allow_override_fee: z.boolean(),
        addressRequiredForCardPayment: z.boolean().default(true),
        surcharge_pct: z.number().nonnegative().nullable(),
        convenience_fee: z.number().nonnegative().nullable(),
    });

export const PaymentRequestAccessTokenExecutionScopeSchema: z.ZodType<PaymentRequestAccessTokenExecutionScope> =
    z.object({
        ipg_id: z.literal(IpgSystemId.VALOR_US),
        terminal_device_id: z.number().int().positive(),
        payment_configuration: PaymentRequestAccessTokenPaymentConfigurationSnapshotSchema,
    });

export const PaymentRequestAccessTokenPayloadSchema: z.ZodType<PaymentRequestAccessTokenPayload> =
    z.object({
        payment_request_uuid: z.string().uuid(),
        payment_execution_scope: PaymentRequestAccessTokenExecutionScopeSchema,
    });

export const PillarLoginLinkTokenPayloadSchema: z.ZodType<PillarLoginLinkTokenPayload> = z
    .object({
        active_role_assignment_id: z.number().int().positive(),
    })
    .strict();

export const SingleUseAuthTokenTypeSchema: z.ZodType<SingleUseAuthTokenType> = z.enum([
    'INVITATION',
    'PASSWORD_RESET',
    'EMAIL_VERIFICATION',
    'SMS_VERIFICATION',
    'PAYMENT_REQUEST_ACCESS',
    'PILLAR_LOGIN_LINK',
    'MFA_ENABLE_EMAIL',
    'MFA_ENABLE_SMS',
    'MFA_LOGIN_EMAIL',
    'MFA_LOGIN_SMS',
]);

export const SingleUseTokenPaymentRequestSummarySchema: z.ZodType<SingleUseTokenPaymentRequestSummary> =
    z.object({
        payment_request_uuid: z.string().uuid(),
        merchant_organization_uuid: z.string().uuid(),
        status: z.enum(PaymentRequestStatus),
        customer_name: z.string().optional(),
        recipient_email: z.string().email().optional(),
        recipient_phone: PhoneNumberSchema.optional(),
        original_amount: z.number().optional(),
        currency_code: z.string().min(1).optional(),
    });

export const SingleUseTokenOrganizationSummarySchema: z.ZodType<SingleUseTokenOrganizationSummary> =
    z.object({
        organization_uuid: z.string().uuid(),
        name: z.string().min(1),
    });

export const SingleUseTokenListItemSchema: z.ZodType<SingleUseTokenListItem> = z.object({
    single_use_auth_token_uuid: z.string().uuid(),
    type: SingleUseAuthTokenTypeSchema,
    user_uuid: z.string().uuid().nullable(),
    user_primary_email: z.string().nullable(),
    email: z.string().email().nullable(),
    organizations: z.array(SingleUseTokenOrganizationSummarySchema),
    payment_request: SingleUseTokenPaymentRequestSummarySchema.optional(),
    created_ts: z.string().datetime(),
    expire_ts: z.string().datetime(),
    used_ts: z.string().datetime().nullable(),
    is_outstanding: z.boolean(),
    is_expired: z.boolean(),
});

export const ListSingleUseTokensRequestSchema: z.ZodType<ListSingleUseTokensRequest> = z.object({
    outstanding_only: z.boolean(),
    q: z.string().trim().optional(),
    limit: z.number().int().positive().max(500).optional(),
    cursor: z.string().uuid().optional(),
});

export const SingleUseTokensSearchRequestSchema: z.ZodType<SingleUseTokensSearchRequest> = z
    .object({
        outstanding_only: z.boolean(),
        terms: SearchTermsSchema,
        limit: z.number().int().positive().max(500).optional(),
        cursor: z.string().uuid().optional(),
    })
    .strict();

export const ListSingleUseTokensResponseSchema: z.ZodType<ListSingleUseTokensResponse> = z.object({
    tokens: z.array(SingleUseTokenListItemSchema),
    pagination: OpaqueCursorPaginationSchema,
});

export const SingleUseAuthTokenUuidParamSchema: z.ZodType<string> = z.string().uuid();

export const SwapCookieForTokenResponseSchema: z.ZodType<SwapCookieForTokenResponse> = z
    .object({
        token: z.string().min(1),
        expires_at: z.string().datetime(),
    })
    .strict();

export const SwapTokenForCookieRequestSchema: z.ZodType<SwapTokenForCookieRequest> = z
    .object({
        token: z.string().min(1),
    })
    .strict();

export const SwapTokenForCookieResponseSchema: z.ZodType<SwapTokenForCookieResponse> =
    LoginResponseSchema;

/**
 * Change password request
 */
export const ChangePasswordRequestSchema = z.object({
    old_password: z.string().min(1),
    new_password: z.string().min(1),
});

/**
 * Setup TOTP response
 */
export const SetupTotpResponseSchema: z.ZodType<SetupTotpResponse> = z.object({
    secret: z.string(),
    qr_code: z.string(),
    message: z.string(),
});

/**
 * Enable TOTP request
 */
export const EnableTotpRequestSchema: z.ZodType<EnableTotpRequest> = z.object({
    secret: z.string(),
    code: z.string().min(1),
});

/**
 * Generate backup codes response
 */
export const GenerateBackupCodesResponseSchema = z.object({
    codes: z.array(z.string()),
});

export const SetDefaultRoleAssignmentUuidRequestSchema: z.ZodType<SetDefaultRoleAssignmentUuidRequest> =
    z.object({
        role_assignment_uuid: z.string().uuid(),
    });

/**
 * Set default role assignment request
 */
export const SetDefaultRoleAssignmentRequestSchema: z.ZodType<SetDefaultRoleAssignmentRequest> =
    SetDefaultRoleAssignmentUuidRequestSchema;

/**
 * Set active role assignment request
 */
export const SetActiveRoleAssignmentRequestSchema = z.object({
    role_assignment_id: z.number(),
});

export const SetActiveRoleAssignmentUuidRequestSchema: z.ZodType<SetActiveRoleAssignmentUuidRequest> =
    z.object({
        role_assignment_uuid: z.string().uuid(),
    });

export const SetActiveRoleAssignmentResponseSchema: z.ZodType<SetActiveRoleAssignmentResponse> =
    z.object({
        message: z.string(),
        requires_totp_setup: z.boolean().default(false),
        totp_setup_activation_target: TotpSetupActivationTargetSchema.optional(),
    });

export const StartImpersonationRequestSchema = z.object({
    role_assignment_uuid: z.string().uuid(),
});

export const ImpersonationContextResponseSchema = z.object({
    impersonated_primary_email: z.string(),
    impersonated_role_assignment_uuid: z.string().uuid(),
    impersonated_role: z.string(),
    impersonated_organization_name: z.string().nullable(),
});

/**
 * Send verification email response
 */
export const SendVerificationEmailResponseSchema = z.object({
    message: z.string(),
    verification_link: z.string(),
});

/**
 * Request password reset request
 */
export const RequestPasswordResetRequestSchema = z.object({
    email: z.string().email(),
});

/**
 * Request password reset response
 */
export const RequestPasswordResetResponseSchema = z.object({
    success: z.boolean(),
    message: z.string(),
    reset_link: z.string().optional(), // For MVP manual distribution
});

export const ValidatePasswordResetTokenRequestSchema: z.ZodObject<{
    token: z.ZodString;
}> = z.object({
    token: z.string().min(1),
});

export const ValidatePasswordResetTokenResponseSchema: z.ZodObject<{
    valid: z.ZodBoolean;
    message: z.ZodString;
}> = z.object({
    valid: z.boolean(),
    message: z.string(),
});

/**
 * Reset password request
 */
export const ResetPasswordRequestSchema = z.object({
    token: z.string().min(1),
    newPassword: z.string().min(1),
});

/**
 * Reset password response
 */
export const ResetPasswordResponseSchema = z.object({
    success: z.boolean(),
    message: z.string(),
});

/**
 * Verify and enable TOTP response
 */
export const VerifyAndEnableTotpResponseSchema = z.object({
    message: z.string(),
    backup_codes: z.array(z.string()),
});

/**
 * Expire session admin response
 */
export const ExpireSessionAdminResponseSchema = z.object({
    message: z.string(),
    user_email: z.string().optional(),
});
